import React from 'react';
import { Box, Button, Divider, Link, Stack } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { SubContentProps } from '../LanguageSetting';
import useShoppingMallVerificationProps from './@hooks/useShoppingMallVerificationProps';
import FormProvider, {
  FormProviderProps,
} from '@/components/HookForm/FormProvider';
import ContentDescription, {
  ContentDivider,
} from '@/components/MenuDrawer/MyMenuDrawer/@components/ContentDescription';
import { Body1, Caption } from '@/components/@IntranetLibrary/Typography';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import Label from '@/components/@IntranetLibrary/Label';
import RHFPasswordField from '@/components/HookForm/RHFPasswordField';
import EmptyContent from '@/components/@IntranetLibrary/EmptyContent';
import RHFTextField from '@/components/HookForm/RHFTextField';
import { DescriptionInner } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/ShoppingMallVerification/style';
import DotList from '@/components/@IntranetLibrary/List/DotList';
import { ListItem } from '@/components/@IntranetLibrary/List/ListItem';
import ProfileCardPopper from '@/components/@IntranetLibrary/ProfileCard/ProfileCardPopper';
import usePopper from '@/hooks/usePopper';
import { isProd } from '@/constant';
import { ShoppingMallVerificationForm } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/ShoppingMallVerification/@hooks/useShoppingMallVerificationForm';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { useTranslation } from 'react-i18next';

export type ShoppingMallVerificationProps = {
  readonly formProps: FormProviderProps<ShoppingMallVerificationForm>;
  readonly isLoading: boolean;
  readonly isSubmitButtonDisabled: boolean;
  readonly verificationStatus: 'loading' | 'success' | 'error';
  readonly verificationInfo: { label: string; value: string }[];
};

const ShoppingMallVerification = (props: SubContentProps) => {
  const { t } = useTranslation();
  const {
    formProps,
    isSubmitButtonDisabled,
    isLoading,
    verificationStatus,
    verificationInfo,
  } = useShoppingMallVerificationProps(props);
  const mypageEventTracker = useMypageEventTracker();

  const passportId = isProd
    ? '6549ee8246c21937d00af3ec'
    : '6549f434794d98e9b2029b4c';
  const { anchorEl, openPopper, closePopper } = usePopper();

  return (
    <>
      <ContentDivider>
        <ContentDescription
          hasVerticalMargin={true}
          divider={<Divider orientation="horizontal" flexItem />}
        >
          <DescriptionInner>
            <h2>{t('MYPAGE.바닐라닷컴에 처음 가입하는 경우')}</h2>
            <p>
              1. {t('MYPAGE.F&F 통합회원 가입')} (
              {t('MYPAGE.택 {{n}}', { n: 1 })})
              <DotList sx={{ marginLeft: '20px' }}>
                <ListItem>
                  <Link
                    href={'https://www.discovery-expedition.com'}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    discovery-expedition
                  </Link>
                </ListItem>
                <ListItem>
                  <Link
                    href={'https://www.mlb-korea.com'}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    mlb-korea
                  </Link>
                </ListItem>
              </DotList>
            </p>
            <p>2. {t('MYPAGE.가입한 계정 정보를 아래에 입력')}</p>
            <p>3. {t('MYPAGE.다음날 바닐라닷컴에 가입')}</p>
          </DescriptionInner>
          <DescriptionInner>
            <h2>{t('MYPAGE.이미 바닐라닷컴에 가입한 경우')}</h2>
            <p>
              1.{' '}
              <Link
                href={'https://www.banila.com'}
                target={'_blank'}
                rel="noreferrer"
              >
                {t('MYPAGE.바닐라닷컴')}
              </Link>{' '}
              {t('MYPAGE.탈퇴')}
            </p>
            <p>
              2. {t('MYPAGE.F&F 통합회원 가입')} (
              {t('MYPAGE.택 {{n}}', { n: 1 })})
              <DotList sx={{ marginLeft: '20px' }}>
                <ListItem>
                  <Link
                    href={'https://www.discovery-expedition.com'}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    discovery-expedition
                  </Link>
                </ListItem>
                <ListItem>
                  <Link
                    href={'https://www.mlb-korea.com'}
                    target={'_blank'}
                    rel="noreferrer"
                  >
                    mlb-korea
                  </Link>
                </ListItem>
              </DotList>
            </p>
            <p>
              3. {t('MYPAGE.제갈애진 과장에게 바닐라닷컴 가입 문의')}
              <DotList sx={{ marginLeft: '20px' }}>
                <ListItem>
                  {t('MYPAGE.생성하신 통합회원 정보를 기억해주세요.')}
                </ListItem>
              </DotList>
            </p>
          </DescriptionInner>
          <DescriptionInner>
            <h2>{t('문의')}</h2>
            <p>
              • {t('MYPAGE.임직원 인증')}:{' '}
              <Link
                href={
                  'https://fnf.atlassian.net/servicedesk/customer/portal/50/group/154/create/813'
                }
                target={'_blank'}
                rel="noreferrer"
              >
                {t('MYPAGE.서비스 데스크')}
              </Link>
              <span> / {t('MYPAGE.회원관리플랫폼(MMP)')}</span>
            </p>
            <p>
              • {t('MYPAGE.바닐라닷컴')} {t('인증')}:{' '}
              <Link
                onClick={(event) => {
                  openPopper(event);

                  // GA Mypage - 25번
                  mypageEventTracker(MypageEventName.viewProfile, {
                    area_name: '바닐라닷컴 직원할인 인증',
                    employees: '제갈애진 과장',
                  });
                }}
                sx={{ cursor: 'pointer' }}
              >
                {t('MYPAGE.F&CO 디지털팀 제갈애진 과장')}
              </Link>
            </p>
          </DescriptionInner>
        </ContentDescription>

        <FormProvider {...formProps}>
          <Stack sx={{ padding: '24px 0 32px', gap: '24px' }}>
            <FormGroup gapsize={'s'}>
              <Label
                isRequired={true}
                title={t('MYPAGE.F&F 통합회원 아이디')}
                size={14}
              />
              <RHFTextField
                name={'id'}
                variant={'outlined'}
                placeholder={t('아이디를 입력해주세요.')}
                sx={{
                  // TODO: styling 공통화
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '48px',
                    fontSize: '14px',
                    lineHeight: '20px',
                  },
                }}
                slotProps={{ htmlInput: { maxLength: 15 } }}
              />
            </FormGroup>
            <FormGroup gapsize={'s'}>
              <Label isRequired={true} title={t('비밀번호')} size={14} />
              <RHFPasswordField
                name={'password'}
                placeholder={t('비밀번호를 입력해주세요.')}
                disabled={isLoading}
                slotProps={{ htmlInput: { maxLength: 12 } }}
              />
            </FormGroup>
            <Button
              variant={'contained'}
              size={'large'}
              type={'submit'}
              disabled={isSubmitButtonDisabled}
              loading={isLoading}
              fullWidth
              onClick={() => {
                // GA Mypage - 26번
                mypageEventTracker(MypageEventName.clickEvent, {
                  area_name: '바닐라닷컴 직원할인 인증',
                  contents: '인증',
                  status: undefined,
                  description: 'F&F 통합계정 인증',
                });
              }}
            >
              {t('인증')}
            </Button>
          </Stack>
        </FormProvider>
        <Stack sx={{ padding: '24px 0 80px', gap: '16px' }}>
          <Body1>{t('인증 정보')}</Body1>
          {(() => {
            switch (verificationStatus) {
              case 'loading':
                return (
                  <Skeleton
                    variant={'text'}
                    width={'100%'}
                    height={30}
                    count={3}
                    gap={'12px'}
                  />
                );
              case 'error':
                return (
                  <EmptyContent
                    title={t('에러가 발생했습니다.')}
                    type={'error'}
                  />
                );
              case 'success':
                return (
                  <>
                    {verificationInfo.length > 0 ? (
                      <Stack gap={'8px'}>
                        {verificationInfo.map(({ label, value }) => {
                          return (
                            <Box key={'label'} sx={{ display: 'flex' }}>
                              <Caption sx={{ minWidth: '80px' }}>
                                {t(label)}
                              </Caption>
                              <Caption sx={{ color: 'var(--dk-text, #000)' }}>{value}</Caption>
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <EmptyContent title={t('인증 정보가 없습니다.')} />
                    )}
                  </>
                );
            }
          })()}
        </Stack>
      </ContentDivider>
      <ProfileCardPopper
        anchorEl={anchorEl}
        passportId={passportId}
        onClose={closePopper}
      />
    </>
  );
};

export default ShoppingMallVerification;
